import type React from "react";
import { Pencil } from "lucide-react";
import { theme, type as text, btnSecondary, card } from "../theme";

// The read-only view of one register record: look first, change on purpose.
//
// Every register opens a record into this view (a modal or an expanded card), never straight
// into a form. The fields read as label / value pairs; anything that changes the record sits
// behind "Edit" or in the separate Actions block at the foot, so reading a record can never
// alter it by accident. People without write permission see the same view with no Edit and no
// Actions. Pattern: docs/NAVIGATION.md "Viewing a record".

export interface DetailField {
  label: string;
  /** Rendered as given; null, undefined and "" show an em dash. */
  value: React.ReactNode;
  /** Take the full row (long text: descriptions, root cause, notes). */
  wide?: boolean;
  /** Keep line breaks in long text. */
  multiline?: boolean;
}

const isEmpty = (v: React.ReactNode) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

/** Label / value pairs in two columns (one on a narrow screen). */
export function DetailFields({ fields }: { fields: DetailField[] }) {
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px 24px", margin: 0 }}>
      {fields.map((f) => (
        <div key={f.label} style={{ gridColumn: f.wide ? "1 / -1" : undefined, minWidth: 0 }}>
          <dt style={{ ...text.label, marginBottom: 2 }}>{f.label}</dt>
          <dd style={{ ...text.body, margin: 0, whiteSpace: f.multiline ? "pre-wrap" : undefined, overflowWrap: "anywhere", color: isEmpty(f.value) ? theme.color.faint : text.body.color }}>
            {isEmpty(f.value) ? "—" : f.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A titled block inside a record view: a drawing sheet with a ruled title strip, the same frame
 * as the main app's panels (`card` / `SheetCard`), so an opened record reads as a set of boxes.
 */
export function DetailSection({ title, children, aside }: { title: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <section style={{ ...card, padding: 0, marginBottom: 12, minWidth: 0 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "8px 14px", borderBottom: "1px solid var(--c-line)", minHeight: 36 }}>
        <h3 style={{ ...text.sectionTitle, margin: 0 }}>{title}</h3>
        {aside}
      </header>
      <div style={{ padding: 14 }}>{children}</div>
    </section>
  );
}

/** The one Edit control a record view carries; only rendered for people who may edit. */
export function EditButton({ onClick, label = "Edit" }: { onClick: () => void; label?: string }) {
  return (
    <button type="button" onClick={onClick} style={{ ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize, display: "inline-flex", alignItems: "center", gap: 6 }}>
      <Pencil size={13} /> {label}
    </button>
  );
}

/**
 * The actions that change a record's state (mark closed, record a notification, raise an
 * action), kept together at the foot of the view under their own heading so they are chosen,
 * never brushed past while reading.
 */
export function RecordActions({ children, note }: { children: React.ReactNode; note?: string }) {
  return (
    <section style={{ marginTop: 6, padding: "12px 14px", borderRadius: theme.radius.md, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>
      <div style={{ ...text.label, marginBottom: 8 }}>Actions</div>
      {note && <div style={{ ...text.hint, marginBottom: 8 }}>{note}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>{children}</div>
    </section>
  );
}
